import './examTables.css';

export const MATH_SCROLL_CLASS = 'exam-math-scroll';

// Under a pixel of difference is rounding, not maths that does not fit.
const TOLERANCE_PX = 1;

// An element with no display reported (jsdom, for one) is taken as inline, as
// an element whose display nothing sets is.
const isInline = (el) => {
  const display = window.getComputedStyle(el).display || 'inline';
  return display.startsWith('inline') || display === 'contents';
};

const px = (value) => parseFloat(value) || 0;

/** The width a block box gives the maths inside it: its own width less its padding. */
const innerWidth = (el) => {
  const s = window.getComputedStyle(el);
  return el.clientWidth - px(s.paddingLeft) - px(s.paddingRight);
};

/** A formula's own width, which does not depend on the line it is on.
 *
 * Usually that is the width of its typeset box (mjx-math). An equation with a
 * \tag is different: MathJax lays it out full width (width="full"), so that
 * the tag sits at the right-hand edge of the line, and its box stretches to
 * whatever it is given. What it needs is the min-width MathJax gives the box
 * inside (mjx-mtable), which holds the equation and its tag. That min-width is
 * written on the box itself and not on the container, so marking the
 * container (examTables.css takes its min-width away) does not change it. */
const naturalWidth = (math) => {
  if (math.getAttribute('width') === 'full') {
    const needed = [math, ...math.querySelectorAll('[width="full"]')]
      .map((el) => window.getComputedStyle(el).minWidth)
      .filter((value) => /px$/.test(value || ''))
      .map(px);
    if (needed.length && Math.max(...needed) > 0) return Math.max(...needed);
  }
  return math.getBoundingClientRect().width;
};

// The side margins each container has when it is not marked. A marked one
// gives up its indent (examTables.css), so its margins then are not the ones
// that decided whether it fits.
const ownMargins = new WeakMap();

/** Whether one MathJax container is wider than the line it sits on.
 *
 * The maths' natural width is its typeset box (mjx-math, MathJax's first child
 * of the container), or for an equation with a tag the width MathJax says it
 * needs (naturalWidth); either way it keeps its width whether or not the
 * container scrolls. The room is the content width of the nearest block round
 * the container, less the container's own side margins (a display equation may
 * be indented) as they are when it is not marked, so the answer never depends
 * on the class this sets. An inline container wider than that cannot fit on any
 * line, so it already stands on a line of its own; one that fits is left
 * inline and untouched. */
const measure = (container) => {
  const math = container.firstElementChild;
  let holder = container.parentElement;
  while (holder && isInline(holder)) holder = holder.parentElement;
  if (!math || !holder) return null;
  const room = innerWidth(holder);
  if (!(room > 0)) return null; // not laid out (hidden, or not in the page): leave it as it is
  if (!container.classList.contains(MATH_SCROLL_CLASS) || !ownMargins.has(container)) {
    const s = window.getComputedStyle(container);
    ownMargins.set(container, px(s.marginLeft) + px(s.marginRight));
  }
  const available = room - ownMargins.get(container);
  return { holder, wide: naturalWidth(math) > available + TOLERANCE_PX };
};

let resizeObserver = null;
let pending = null;
const watched = new WeakSet();

const watch = (holder) => {
  if (watched.has(holder) || typeof window.ResizeObserver !== 'function') return;
  if (!resizeObserver) {
    // The work waits for the next frame: marking changes the height of what
    // is observed, and doing it inside the callback would be reported as a
    // ResizeObserver loop.
    resizeObserver = new window.ResizeObserver((entries) => {
      if (!pending) {
        pending = new Set();
        window.requestAnimationFrame(() => {
          const blocks = pending;
          pending = null;
          blocks.forEach((el) => {
            if (el.isConnected) {
              markWideMath(el);
            } else {
              resizeObserver.unobserve(el);
              watched.delete(el);
            }
          });
        });
      }
      entries.forEach((entry) => pending.add(entry.target));
    });
  }
  watched.add(holder);
  resizeObserver.observe(holder);
};

/** Make each typeset formula in `root` that is wider than its line scroll
 * sideways inside the question (examTables.css), and put back any that fits
 * again. Call it after MathJax has typeset `root`.
 *
 * At 390 px a table written in LaTeX (an array or tabular in $…$ or $$…$$)
 * ran off the right edge of the question, with no way to see its last
 * columns. A formula that fits is not touched, so ordinary inline maths stays
 * inline and keeps its baseline: a scroll box would move the baseline to its
 * bottom edge. Maths inside an HTML table is left alone, because the table's
 * own box scrolls (wrapTablesForScroll).
 *
 * All the measuring is done before any class changes, so the page is laid out
 * once. The block each formula sits in is then watched, and its formulas are
 * measured again whenever its size changes (a phone turned, the split between
 * passage and question dragged); a block that leaves the page stops being
 * watched.
 *
 * Returns how many formulas now scroll. */
export function markWideMath(root) {
  if (!root || typeof root.querySelectorAll !== 'function' || typeof window === 'undefined') return 0;
  const containers = Array.from(root.querySelectorAll('mjx-container')).filter((c) => !c.closest('table'));
  const verdicts = containers.map(measure);
  let wide = 0;
  containers.forEach((container, i) => {
    const verdict = verdicts[i];
    if (!verdict) return;
    container.classList.toggle(MATH_SCROLL_CLASS, verdict.wide);
    if (verdict.wide) wide += 1;
    watch(verdict.holder);
  });
  return wide;
}
